import React, {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import introImage from '../../assets/02.JPG';
import {
  DEFAULT_COVER_REVEAL_TIMING,
  getCoverRevealProgress,
  getViewportRevealTriggerPosition,
  type CoverRevealTiming,
} from './letterRevealTiming';
import './LetterRevealSection.css';

gsap.registerPlugin(ScrollTrigger);

export type LetterRevealCopySegment = {
  text: string;
  isHighlight?: boolean;
  href?: string;
  ariaLabel?: string;
};

export type LetterRevealCopy = LetterRevealCopySegment[][];

type LetterRevealSpaceToken = {
  type: 'space';
  text: string;
  href?: string;
  linkLabel?: string;
};

type LetterRevealWordToken = {
  type: 'word';
  text: string;
  isHighlight: boolean;
  href?: string;
  linkLabel?: string;
  chars: {
    value: string;
    revealOrder: number;
    isHighlight: boolean;
  }[];
};

type LetterRevealToken = LetterRevealSpaceToken | LetterRevealWordToken;

type LetterRevealVariant = 'full' | 'cover' | 'follow' | 'handoff';

export type LetterRevealCoverImageMetrics = {
  visibleImageOffset: number;
  imageHeight: number;
};

export type LetterRevealSectionHandle = {
  setRevealProgress: (progress: number) => void;
};

type LetterRevealSectionProps = {
  variant?: LetterRevealVariant;
  copy?: LetterRevealCopy;
  footer?: React.ReactNode;
  visibleImageOffset?: number;
  imageHeight?: number;
  onCoverImageMetricsChange?: (metrics: LetterRevealCoverImageMetrics) => void;
  coverRevealTiming?: CoverRevealTiming;
  isRevealExternallyDriven?: boolean;
};

type LetterRevealCopyBlockProps = {
  className?: string;
  copy: LetterRevealCopy;
  footer?: React.ReactNode;
  copyRef?: React.Ref<HTMLDivElement>;
  coverRevealTiming?: CoverRevealTiming;
  isRevealExternallyDriven?: boolean;
};

export const DEFAULT_LETTER_REVEAL_COPY: LetterRevealCopy = [
  [
    { text: 'hi, my name is ' },
    { text: 'mia.', isHighlight: true },
  ],
  [
    { text: "i'm a " },
    { text: 'master of it', isHighlight: true },
    { text: ' student at the ' },
    { text: 'university of auckland' },
    { text: ', with 4 years of ' },
    { text: 'creative experience in advertising.', isHighlight: true },
  ],
];

const REVEAL_START_VIEWPORT_RATIO = 0.8;
const REVEAL_END_POSITION = 'top top';
const HANDOFF_MIN_WIDTH = 810;
const VISUAL_LINE_TOP_TOLERANCE = 2;
const LINE_REVEAL_OVERLAP = 3.25;

const clampRevealProgress = (progress: number) => {
  if (!Number.isFinite(progress)) {
    return 0;
  }

  return Math.min(1, Math.max(0, progress));
};

const smoothstep = (progress: number) => {
  const clampedProgress = clampRevealProgress(progress);

  return clampedProgress * clampedProgress * (3 - 2 * clampedProgress);
};

export const resolveLineRevealProgress = (
  overallProgress: number,
  lineIndex: number,
  lineCount: number
) => {
  const safeLineCount = Math.max(1, Math.floor(lineCount));
  const safeLineIndex = Math.min(
    safeLineCount - 1,
    Math.max(0, Math.floor(lineIndex))
  );
  const timelineLength = Math.max(
    LINE_REVEAL_OVERLAP,
    safeLineCount - 1 + LINE_REVEAL_OVERLAP
  );
  const localProgress =
    (clampRevealProgress(overallProgress) * timelineLength - safeLineIndex) /
    LINE_REVEAL_OVERLAP;

  return smoothstep(localProgress);
};

const buildParagraphs = (copy: LetterRevealCopy) => {
  let revealOrder = 0;

  return copy.map((paragraph) => ({
    text: paragraph.map((segment) => segment.text).join(''),
    tokens: paragraph.flatMap((segment: LetterRevealCopySegment) =>
      (segment.text.match(/\S+|\s+/g) ?? []).map((token) => {
        const linkLabel = segment.ariaLabel ?? segment.text.trim();

        if (/^\s+$/.test(token)) {
          return {
            type: 'space' as const,
            text: token,
            href: segment.href,
            linkLabel,
          };
        }

        return {
          type: 'word' as const,
          text: token,
          isHighlight: Boolean(segment.isHighlight),
          href: segment.href,
          linkLabel,
          chars: Array.from(token).map((char) => ({
            value: char,
            revealOrder: revealOrder++,
            isHighlight: Boolean(segment.isHighlight),
          })),
        };
      })
    ),
  }));
};

const renderParagraphToken = (token: LetterRevealToken, tokenKey: string) => {
  if (token.type === 'space') {
    return (
      <React.Fragment key={tokenKey}>
        {token.text}
      </React.Fragment>
    );
  }

  return (
    <span
      key={tokenKey}
      className={`letter-reveal-word${token.isHighlight ? ' is-highlight' : ''}`}
      aria-hidden="true"
    >
      {token.chars.map((char, charIndex) => (
        <span
          key={`${tokenKey}-${charIndex}`}
          className={`letter-reveal-char${char.isHighlight ? ' is-highlight' : ''}`}
          data-reveal-order={char.revealOrder}
        >
          {char.value}
        </span>
      ))}
    </span>
  );
};

const LetterRevealCopyBlock = forwardRef<
  LetterRevealSectionHandle,
  LetterRevealCopyBlockProps
>(function LetterRevealCopyBlock({
  className = '',
  copy,
  footer,
  copyRef,
  coverRevealTiming = DEFAULT_COVER_REVEAL_TIMING,
  isRevealExternallyDriven = false,
}, ref) {
  const internalCopyRef = useRef<HTMLDivElement | null>(null);
  const latestProgressRef = useRef(0);
  const updateLineRevealRef = useRef<(progress: number) => void>(() => undefined);
  const paragraphs = useMemo(() => buildParagraphs(copy), [copy]);
  const {
    phase: coverRevealPhase,
    startFraction: coverRevealStartFraction,
    endFraction: coverRevealEndFraction,
  } = coverRevealTiming;

  const setRevealProgress = useCallback((progress: number) => {
    const nextProgress = clampRevealProgress(progress);
    latestProgressRef.current = nextProgress;
    updateLineRevealRef.current(nextProgress);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      setRevealProgress,
    }),
    [setRevealProgress]
  );

  const setCopyNode = (node: HTMLDivElement | null) => {
    internalCopyRef.current = node;

    if (!copyRef) {
      return;
    }

    if (typeof copyRef === 'function') {
      copyRef(node);
      return;
    }

    (copyRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  };

  useLayoutEffect(() => {
    if (!internalCopyRef.current) {
      return;
    }

    const panel = internalCopyRef.current;
    const coverStage = panel.closest<HTMLElement>('[data-cover-stage]');
    const revealSection = panel.closest<HTMLElement>('.letter-reveal-section');
    const useCoverStage =
      Boolean(coverStage) &&
      window.matchMedia(`(min-width: ${HANDOFF_MIN_WIDTH}px)`).matches;

    const triggerTarget = useCoverStage ? coverStage : revealSection ?? panel;
    const chars = Array.from(panel.querySelectorAll<HTMLElement>('.letter-reveal-char'));

    if (chars.length === 0) {
      return;
    }

    const orderedChars = chars
      .map((char, index) => ({
        element: char,
        revealOrder: Number(char.dataset.revealOrder ?? index),
      }))
      .sort((a, b) => a.revealOrder - b.revealOrder);

    let visualLines: HTMLElement[][] = [];

    const measureVisualLines = () => {
      const nextVisualLines: HTMLElement[][] = [];
      let currentLineTop: number | null = null;

      orderedChars.forEach(({ element }) => {
        const elementTop = element.getBoundingClientRect().top;

        if (
          currentLineTop === null ||
          Math.abs(elementTop - currentLineTop) > VISUAL_LINE_TOP_TOLERANCE
        ) {
          nextVisualLines.push([]);
          currentLineTop = elementTop;
        }

        nextVisualLines[nextVisualLines.length - 1].push(element);
      });

      visualLines = nextVisualLines;
      panel.style.setProperty('--letter-reveal-line-count', String(visualLines.length));
    };

    const updateLineReveal = (progress: number) => {
      latestProgressRef.current = clampRevealProgress(progress);

      visualLines.forEach((line, lineIndex) => {
        const lineProgress = resolveLineRevealProgress(
          latestProgressRef.current,
          lineIndex,
          visualLines.length
        );
        const serializedProgress = lineProgress.toFixed(4);

        line.forEach((element) => {
          element.style.setProperty('--letter-line-progress', serializedProgress);
        });
      });
    };

    const measureAndUpdate = () => {
      measureVisualLines();
      updateLineReveal(latestProgressRef.current);
    };

    measureAndUpdate();
    updateLineRevealRef.current = updateLineReveal;

    let resizeFrame = 0;
    const handleResize = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(measureAndUpdate);
    };
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(panel);

    if (isRevealExternallyDriven) {
      return () => {
        window.cancelAnimationFrame(resizeFrame);
        resizeObserver.disconnect();
        updateLineRevealRef.current = () => undefined;
      };
    }

    const holdRatio = Number(coverStage?.dataset.coverHold ?? 0);
    const coverRatio = Number(coverStage?.dataset.coverCover ?? 1);
    const releaseRatio = Number(coverStage?.dataset.coverRelease ?? 0);
    const resolvedCoverRevealTiming: CoverRevealTiming = {
      phase: coverRevealPhase,
      startFraction: coverRevealStartFraction,
      endFraction: coverRevealEndFraction,
    };
    const trigger = ScrollTrigger.create({
      trigger: triggerTarget,
      start: useCoverStage
        ? 'top top'
        : () =>
            getViewportRevealTriggerPosition(
              window.innerHeight,
              REVEAL_START_VIEWPORT_RATIO
            ),
      end: useCoverStage
        ? () => `+=${window.innerHeight * (holdRatio + coverRatio + releaseRatio)}`
        : REVEAL_END_POSITION,
      scrub: useCoverStage ? 1 : 0.65,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        if (useCoverStage) {
          updateLineReveal(
            getCoverRevealProgress({
              stageProgress: self.progress,
              holdRatio,
              coverRatio,
              releaseRatio,
              coverRevealTiming: resolvedCoverRevealTiming,
            })
          );
          return;
        }

        updateLineReveal(self.progress);
      },
      onRefresh: (self) => {
        measureVisualLines();

        if (useCoverStage) {
          updateLineReveal(
            getCoverRevealProgress({
              stageProgress: self.progress,
              holdRatio,
              coverRatio,
              releaseRatio,
              coverRevealTiming: resolvedCoverRevealTiming,
            })
          );
          return;
        }

        updateLineReveal(self.progress);
      },
    });

    if (useCoverStage) {
      updateLineReveal(
        getCoverRevealProgress({
          stageProgress: trigger.progress,
          holdRatio,
          coverRatio,
          releaseRatio,
          coverRevealTiming: resolvedCoverRevealTiming,
        })
      );
    } else {
      updateLineReveal(trigger.progress);
    }

    return () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeObserver.disconnect();
      updateLineRevealRef.current = () => undefined;
      trigger.kill();
    };
  }, [
    copy,
    coverRevealEndFraction,
    coverRevealPhase,
    coverRevealStartFraction,
    isRevealExternallyDriven,
  ]);

  const copyClassName = ['letter-reveal-copy', className].filter(Boolean).join(' ');

  return (
    <div ref={setCopyNode} className={copyClassName}>
      {paragraphs.map((paragraph, paragraphIndex) => (
        <p
          key={paragraphIndex}
          className="letter-reveal-line"
          aria-label={paragraph.text}
        >
          {paragraph.tokens.map((token, tokenIndex) => {
            const tokenKey = `${paragraphIndex}-${tokenIndex}`;

            if (!token.href) {
              return renderParagraphToken(token, tokenKey);
            }

            const previousToken = paragraph.tokens[tokenIndex - 1];
            const isContinuingLink =
              previousToken?.href === token.href &&
              previousToken?.linkLabel === token.linkLabel;

            if (isContinuingLink) {
              return null;
            }

            const linkedTokens: LetterRevealToken[] = [];

            for (let index = tokenIndex; index < paragraph.tokens.length; index += 1) {
              const currentToken = paragraph.tokens[index];

              if (
                currentToken.href !== token.href ||
                currentToken.linkLabel !== token.linkLabel
              ) {
                break;
              }

              linkedTokens.push(currentToken);
            }

            return (
              <a
                key={tokenKey}
                className="letter-reveal-link"
                href={token.href}
                aria-label={token.linkLabel}
              >
                {linkedTokens.map((linkedToken, linkedTokenIndex) => (
                  renderParagraphToken(
                    linkedToken,
                    `${tokenKey}-${linkedTokenIndex}`
                  )
                ))}
              </a>
            );
          })}
        </p>
      ))}
      {footer}
    </div>
  );
});

export const LetterRevealSection = forwardRef<
  LetterRevealSectionHandle,
  LetterRevealSectionProps
>(function LetterRevealSection({
  variant = 'full',
  copy = DEFAULT_LETTER_REVEAL_COPY,
  footer,
  visibleImageOffset = 0,
  imageHeight = 0,
  onCoverImageMetricsChange,
  coverRevealTiming = DEFAULT_COVER_REVEAL_TIMING,
  isRevealExternallyDriven = false,
}, ref) {
  const sectionRef = useRef<HTMLElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const copyBlockRef = useRef<LetterRevealSectionHandle>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageWrapRef = useRef<HTMLDivElement>(null);
  const latestProgressRef = useRef(0);

  const setRevealProgress = useCallback((progress: number) => {
    const nextProgress = clampRevealProgress(progress);
    latestProgressRef.current = nextProgress;
    copyBlockRef.current?.setRevealProgress(nextProgress);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      setRevealProgress,
    }),
    [setRevealProgress]
  );

  useEffect(() => {
    if (variant !== 'cover' || !onCoverImageMetricsChange) {
      return;
    }

    if (!sectionRef.current || !imageRef.current || !imageWrapRef.current) {
      return;
    }

    const imageNode = imageRef.current;
    let frameId = 0;

    const updateMetrics = () => {
      if (!sectionRef.current || !imageNode || !imageWrapRef.current) {
        return;
      }

      const sectionHeight = sectionRef.current.clientHeight;
      const imageTop = imageWrapRef.current.offsetTop;
      const measuredImageHeight = imageNode.offsetHeight;
      const measuredVisibleOffset = Math.min(
        measuredImageHeight,
        Math.max(0, sectionHeight - imageTop)
      );

      onCoverImageMetricsChange({
        imageHeight: Math.round(measuredImageHeight),
        visibleImageOffset: Math.round(measuredVisibleOffset),
      });
    };

    const requestMetricsUpdate = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateMetrics);
    };

    const resizeObserver = new ResizeObserver(() => {
      requestMetricsUpdate();
    });

    resizeObserver.observe(sectionRef.current);
    resizeObserver.observe(imageNode);

    if (copyRef.current) {
      resizeObserver.observe(copyRef.current);
    }

    const handleResize = () => {
      requestMetricsUpdate();
    };

    requestMetricsUpdate();

    window.addEventListener('resize', handleResize);
    imageNode.addEventListener('load', requestMetricsUpdate);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      imageNode.removeEventListener('load', requestMetricsUpdate);
      resizeObserver.disconnect();
    };
  }, [onCoverImageMetricsChange, variant]);

  const sectionClassName = [
    'letter-reveal-section',
    `letter-reveal-section--${variant}`,
  ].join(' ');

  const imageOffsetStyle =
    variant === 'follow' || variant === 'handoff'
      ? ({
          '--intro-image-height': `${Math.max(0, imageHeight)}px`,
          '--intro-visible-image-offset': `${Math.max(0, visibleImageOffset)}px`,
        } as CSSProperties)
      : undefined;

  return (
    <section ref={sectionRef} className={sectionClassName} style={imageOffsetStyle}>
      {variant === 'full' || variant === 'cover' ? (
        <div className="letter-reveal-copy-shell">
          <LetterRevealCopyBlock
            ref={copyBlockRef}
            copy={copy}
            footer={footer}
            copyRef={copyRef}
            coverRevealTiming={coverRevealTiming}
            isRevealExternallyDriven={isRevealExternallyDriven}
          />
        </div>
      ) : null}

      {variant === 'follow' ? (
        <div className="letter-reveal-follow-viewport">
          <div
            ref={imageWrapRef}
            className="letter-reveal-image-wrap letter-reveal-image-wrap--follow"
          >
            <img
              ref={imageRef}
              className="letter-reveal-image"
              src={introImage}
              alt="Mia introduction visual"
            />
          </div>
        </div>
      ) : variant === 'handoff' ? (
        <div className="letter-reveal-handoff-viewport">
          <div
            ref={imageWrapRef}
            className="letter-reveal-image-wrap letter-reveal-image-wrap--handoff"
          >
            <img
              ref={imageRef}
              className="letter-reveal-image"
              src={introImage}
              alt="Mia introduction visual"
            />
          </div>
        </div>
      ) : variant === 'cover' ? (
        <div
          ref={imageWrapRef}
          className={`letter-reveal-image-wrap letter-reveal-image-wrap--${variant}`}
        >
          <img
            ref={imageRef}
            className="letter-reveal-image"
            src={introImage}
            alt="Mia introduction visual"
          />
        </div>
      ) : null}
    </section>
  );
});
